import React, { useEffect, useState, useMemo } from "react";
import { businessDaysUntil } from "../utils/businessDays";
import { PageHeader, Button, Badge, LoadingState, KpiCard } from "./ui";

const API_BASE = "/api";

// 今日から何日後か計算
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

// 待機開始日から何日経過したか計算
function waitingDays(waitingSince) {
  if (!waitingSince) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const since = new Date(waitingSince);
  since.setHours(0, 0, 0, 0);
  return Math.round((today - since) / (1000 * 60 * 60 * 24));
}

// 緊急度の設定
// days: カレンダー日数（フィルタ閾値用）, bizDays: 営業日数（表示用）
function urgencyConfig(days, bizDays) {
  if (days === null) return null;
  const displayDays = bizDays !== undefined && bizDays !== null ? bizDays : days;
  if (days < 0)  return { label: "契約終了済", color: "bg-slate-100 text-slate-500", badge: "bg-slate-400", ring: "ring-slate-300", dot: "bg-slate-400", priority: 0 };
  if (days <= 14) return { label: `残${displayDays}営業日`, color: "bg-red-50 text-red-700", badge: "bg-red-500", ring: "ring-red-300", dot: "bg-red-500", priority: 1 };
  if (days <= 30) return { label: `残${displayDays}営業日`, color: "bg-orange-50 text-orange-700", badge: "bg-orange-400", ring: "ring-orange-300", dot: "bg-orange-400", priority: 2 };
  return null; // 30日超は警告対象外
}

// 延長確認日：契約終了1ヶ月前の日が属する週の月曜日（第1営楮日）
function extensionCheckDate(endDateStr) {
  if (!endDateStr) return null;
  const base = new Date(endDateStr);
  base.setMonth(base.getMonth() - 1);
  const dow = base.getDay(); // 0=日, 1=月...6=土
  const monday = new Date(base);
  if (dow === 0) monday.setDate(monday.getDate() + 1);
  else if (dow === 6) monday.setDate(monday.getDate() + 2);
  else monday.setDate(monday.getDate() - (dow - 1));
  return monday;
}

// 延長確認アラートが必要かチェック（14日以内に確認日が来る or 超過中）
function extensionAlertStatus(endDateStr) {
  const checkDate = extensionCheckDate(endDateStr);
  if (!checkDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const check = new Date(checkDate); check.setHours(0, 0, 0, 0);
  const end = new Date(endDateStr); end.setHours(0, 0, 0, 0);
  const daysToEnd = Math.round((end - today) / 86400000);
  if (daysToEnd <= 0) return null; // 契約終了済めは除外
  const daysToCheck = Math.round((check - today) / 86400000);
  if (daysToCheck > 14) return null; // 2週間超先はまだ表示しない
  // 営業日数（表示用）
  const daysToCheckBiz = businessDaysUntil(check);
  const daysToEndBiz = businessDaysUntil(endDateStr);
  return { checkDate: check, daysToCheck, daysToEnd, daysToCheckBiz, daysToEndBiz };
}

// ────────────────────────────────────────────────
// KPIカード（文字列値用）
// ────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, accent = "primary", pulse = false }) {
  const accentBorder = {
    primary: "border-l-prodia-primary",
    success: "border-l-success",
    warning: "border-l-warning",
    danger: "border-l-danger",
    info: "border-l-info",
    neutral: "border-l-prodia-border",
  };
  return (
    <div className={`bg-prodia-surface border border-prodia-border border-l-4 ${accentBorder[accent] || accentBorder.primary} rounded-xl p-4 shadow-prodia relative`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-medium text-prodia-text-secondary">{label}</span>
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-prodia-background flex items-center justify-center flex-shrink-0">
            <i className={`${icon} text-sm text-prodia-text-muted`}></i>
          </div>
        )}
        {pulse && <span className="flex h-2 w-2 relative"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-warning opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-warning"></span></span>}
      </div>
      <p className="text-2xl font-semibold text-prodia-text-primary">{value}</p>
      {sub && <p className="text-xs text-prodia-text-muted mt-1">{sub}</p>}
    </div>
  );
}

// ────────────────────────────────────────────────
// 契約終了アラートカード（IDR / BP 共通）
// ────────────────────────────────────────────────
function AlertRow({ engineer, days, cfg }) {
  const isBP = engineer._type === 'bp';
  const endDate = isBP ? engineer.contract_end : engineer.project_end_date;
  const dateLabel = endDate
    ? new Date(endDate).toLocaleDateString("ja-JP", { year: "numeric", month: "short", day: "numeric" })
    : "-";
  const rateLabel = isBP
    ? (engineer.client_unit_price && engineer.partner_unit_price
        ? `粗利 ¥${(Number(engineer.client_unit_price) - Number(engineer.partner_unit_price)).toLocaleString()}`
        : "単価未設定")
    : (engineer.monthly_rate ? `¥${Number(engineer.monthly_rate).toLocaleString()}` : "未設定");

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border ${cfg.color} ring-1 ${cfg.ring} transition-all hover:shadow-sm`}>
      {/* 種別ドット */}
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
        <span className={`text-[9px] font-bold px-1.5 rounded ${isBP ? 'bg-violet-100 text-violet-600' : 'bg-emerald-100 text-emerald-600'}`}>
          {isBP ? 'BP' : 'IDR'}
        </span>
      </div>

      {/* 名前・会社 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-800 text-sm">{engineer.name}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${cfg.badge}`}>
            {cfg.label}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
          <i className="fas fa-building text-[10px]"></i>
          {engineer.client_company || "客先未設定"}
          {isBP && engineer.partner_company && (
            <><span className="text-slate-300">·</span>
            <i className="fas fa-handshake text-[10px]"></i>
            {engineer.partner_company}</>
          )}
          <span className="text-slate-300">·</span>
          <i className="fas fa-user text-[10px]"></i>
          {engineer.planner || "-"}
        </p>
      </div>

      {/* 終了日・単価 */}
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 justify-end">
          <i className="fas fa-calendar-times text-[10px] text-slate-400"></i>
          {dateLabel}
        </p>
        <p className="text-[11px] text-slate-500">{rateLabel} / 月</p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// 延長確認アラート行
// ────────────────────────────────────────────────
function ExtensionCheckRow({ engineer }) {
  const isBP = engineer._type === 'bp';
  const endDateStr = isBP ? engineer.contract_end : engineer.project_end_date;
  const { checkDate, daysToCheck, daysToEnd, daysToCheckBiz, daysToEndBiz } = engineer._extStatus;

  const checkDateLabel = checkDate.toLocaleDateString("ja-JP", { month: "short", day: "numeric", weekday: "short" });
  const endDateLabel = new Date(endDateStr).toLocaleDateString("ja-JP", { year: "numeric", month: "short", day: "numeric" });

  // 表示は営業日数、色の閾値はカレンダー日数を使用
  let statusLabel, badgeColor, rowColor, ringColor;
  if (daysToCheck < 0) {
    statusLabel = `${Math.abs(daysToCheckBiz)}日超過`;
    badgeColor = "bg-red-500";
    rowColor = "bg-red-50 text-red-700";
    ringColor = "ring-red-200";
  } else if (daysToCheck === 0) {
    statusLabel = "本日が確認日！";
    badgeColor = "bg-red-500 animate-pulse";
    rowColor = "bg-red-50 text-red-700";
    ringColor = "ring-red-200";
  } else if (daysToCheck <= 3) {
    statusLabel = `${daysToCheckBiz}日後に確認`;
    badgeColor = "bg-orange-400";
    rowColor = "bg-orange-50 text-orange-700";
    ringColor = "ring-orange-200";
  } else {
    statusLabel = `${daysToCheckBiz}日後に確認`;
    badgeColor = "bg-amber-400";
    rowColor = "bg-amber-50 text-amber-700";
    ringColor = "ring-amber-200";
  }

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border ${rowColor} ring-1 ${ringColor} transition-all hover:shadow-sm`}>
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <i className="fas fa-clipboard-check text-sm opacity-60"></i>
        <span className={`text-[9px] font-bold px-1.5 rounded ${isBP ? 'bg-violet-100 text-violet-600' : 'bg-emerald-100 text-emerald-600'}`}>
          {isBP ? 'BP' : 'IDR'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-800 text-sm">{engineer.name}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${badgeColor}`}>
            {statusLabel}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
          <i className="fas fa-building text-[10px]"></i>
          {engineer.client_company || "客先未設定"}
          <span className="text-slate-300">·</span>
          <i className="fas fa-user text-[10px]"></i>
          {engineer.planner || "-"}
          {isBP && engineer.extension_possibility && engineer.extension_possibility !== 'unknown' && (
            <><span className="text-slate-300">·</span>
            <span className={`text-[10px] font-semibold ${engineer.extension_possibility === 'yes' ? 'text-emerald-600' : 'text-slate-400'}`}>
              延長{engineer.extension_possibility === 'yes' ? '済み' : 'なし'}
            </span></>
          )}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 justify-end">
          <i className="fas fa-calendar-check text-[10px] text-amber-400"></i>
          確認日: {checkDateLabel}
        </p>
        <p className="text-[11px] text-slate-500 flex items-center gap-1 justify-end mt-0.5">
          <i className="fas fa-calendar-times text-[10px] text-slate-300"></i>
          終了: {endDateLabel}（残{daysToEndBiz}営業日）
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// 稼働率ゲージ
// ────────────────────────────────────────────────
function UtilizationGauge({ rate }) {
  const clamp = Math.min(100, Math.max(0, rate));
  const label =
    clamp >= 80 ? "優良" :
    clamp >= 60 ? "要注意" :
    "危険";
  const labelColor =
    clamp >= 80 ? "text-emerald-600 bg-emerald-50" :
    clamp >= 60 ? "text-amber-600 bg-amber-50" :
    "text-red-600 bg-red-50";

  // SVG円形ゲージ
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamp / 100);

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative w-40 h-40">
        <svg className="w-40 h-40 -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" />
          <circle
            cx="64" cy="64" r={radius}
            fill="none"
            stroke="url(#gaugeGrad)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 1s ease" }}
          />
          <defs>
            <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={clamp >= 80 ? "#34d399" : clamp >= 60 ? "#fbbf24" : "#f87171"} />
              <stop offset="100%" stopColor={clamp >= 80 ? "#14b8a6" : clamp >= 60 ? "#f97316" : "#ef4444"} />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-slate-800">{clamp.toFixed(0)}<span className="text-lg">%</span></span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full mt-1 ${labelColor}`}>{label}</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2">稼働率（稼働中 / 全体）</p>
    </div>
  );
}

// ────────────────────────────────────────────────
// ステータス別バー
// ────────────────────────────────────────────────
function StatusBreakdown({ engineers }) {
  const counts = useMemo(() => {
    const c = {};
    engineers.forEach((e) => {
      const s = e.engineer_status || "不明";
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [engineers]);

  const total = engineers.length;
  const buckets = [
    { key: "アサイン済", label: "稼働中", color: "bg-emerald-500", textColor: "text-emerald-700", bg: "bg-emerald-50" },
    { key: "未アサイン", label: "待機中", color: "bg-amber-400", textColor: "text-amber-700", bg: "bg-amber-50" },
    { key: "フェードアウト", label: "フェードアウト中", color: "bg-orange-400", textColor: "text-orange-700", bg: "bg-orange-50" },
    { key: "退職", label: "退職", color: "bg-slate-400", textColor: "text-slate-600", bg: "bg-slate-50" },
  ];

  // 上記buckets以外のステータスもまとめて表示
  const knownKeys = buckets.map((b) => b.key);
  const otherCount = Object.entries(counts)
    .filter(([k]) => !knownKeys.includes(k))
    .reduce((s, [, v]) => s + v, 0);

  return (
    <div className="space-y-3">
      {buckets.map((b) => {
        const count = counts[b.key] || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={b.key}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={`font-bold ${b.textColor}`}>{b.label}</span>
              <span className="text-slate-500">{count}名 <span className="text-slate-400">({pct.toFixed(0)}%)</span></span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${b.color} rounded-full transition-all duration-700`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
      {otherCount > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-bold text-slate-500">その他</span>
            <span className="text-slate-500">{otherCount}名</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-slate-300 rounded-full" style={{ width: `${(otherCount / total) * 100}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────
// エンジニア一覧テーブル（全員）
// ────────────────────────────────────────────────
function EngineerTable({ engineers }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewMode, setViewMode] = useState("action"); // "action" | "all"

  const statuses = useMemo(() => {
    const s = new Set(engineers.map((e) => e.engineer_status).filter(Boolean));
    return [...s];
  }, [engineers]);

  const rows = useMemo(() => {
    return engineers
      .map((e) => ({ ...e, _days: daysUntil(e.project_end_date), _bizDays: businessDaysUntil(e.project_end_date), _extStatus: extensionAlertStatus(e.project_end_date) }))
      .filter((e) => {
        // 要対応フィルター: 30日以内に終了 OR 待機中 OR フェードアウト OR 延長確認対象
        if (viewMode === "action") {
          const needsAction =
            (e._days !== null && e._days <= 30) ||
            e.engineer_status === "未アサイン" ||
            e.engineer_status === "フェードアウト" ||
            e._extStatus !== null;
          if (!needsAction) return false;
        }
        if (filterStatus && e.engineer_status !== filterStatus) return false;
        if (search) {
          const q = search.toLowerCase();
          return e.name?.toLowerCase().includes(q) || e.client_company?.toLowerCase().includes(q) || e.planner?.toLowerCase().includes(q);
        }
        return true;
      })
      .sort((a, b) => {
        if (a._days === null && b._days === null) return 0;
        if (a._days === null) return 1;
        if (b._days === null) return -1;
        return a._days - b._days;
      });
  }, [engineers, search, filterStatus, viewMode]);

  // 要対応件数（バッジ用）
  const actionCount = useMemo(() => engineers.filter(e => {
    const d = daysUntil(e.project_end_date);
    return (d !== null && d <= 30) || e.engineer_status === "未アサイン" || e.engineer_status === "フェードアウト" || extensionAlertStatus(e.project_end_date) !== null;
  }).length, [engineers]);

  return (
    <div className="flex flex-col">
      {/* 要対応 / 全員 トグル + フィルター */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* トグル */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode("action")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              viewMode === "action" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className="fas fa-exclamation-circle text-[10px]" />
            要対応のみ
            {actionCount > 0 && (
              <span className={`text-[10px] font-bold px-1.5 rounded-full ${
                viewMode === "action" ? "bg-orange-100 text-orange-600" : "bg-slate-200 text-slate-500"
              }`}>{actionCount}</span>
            )}
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              viewMode === "all" ? "bg-white text-slate-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            全員
          </button>
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
          <input
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            placeholder="名前・客先・担当で検索..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">すべてのステータス</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-slate-400 ml-auto">{rows.length}件</span>
      </div>

      {/* テーブル */}
      <div className="overflow-auto rounded-xl border border-slate-100 shadow-sm bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">氏名</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">ステータス</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">客先</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">担当営業</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">月単価</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">契約終了予定</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">残日数</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-10 text-slate-400 text-sm">
                  {viewMode === "action"
                    ? <span className="flex items-center justify-center gap-2 text-emerald-500"><i className="fas fa-check-circle text-base" />現在、対応が必要なエンジニアはいません</span>
                    : "該当なし"
                  }
                </td>
              </tr>
            ) : rows.map((e) => {
              const cfg = urgencyConfig(e._days, e._bizDays);
              const statusColor =
                e.engineer_status === "アサイン済" ? "bg-emerald-100 text-emerald-700" :
                e.engineer_status === "未アサイン" ? "bg-amber-100 text-amber-700" :
                "bg-slate-100 text-slate-600";

              return (
                <tr key={e.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{e.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor}`}>
                      {e.engineer_status || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.client_company || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.planner || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 font-bold text-slate-700 whitespace-nowrap">
                    {e.monthly_rate
                      ? `¥${Number(e.monthly_rate).toLocaleString()}`
                      : <span className="text-slate-300 font-normal">未設定</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    {e.project_end_date
                      ? new Date(e.project_end_date).toLocaleDateString("ja-JP", { year: "numeric", month: "short", day: "numeric" })
                      : <span className="text-slate-300">未設定</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1 items-start">
                      {e.engineer_status === '未アサイン' && e.waiting_since ? (
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full text-white ${
                          waitingDays(e.waiting_since) >= 30 ? 'bg-red-500' :
                          waitingDays(e.waiting_since) >= 14 ? 'bg-orange-400' : 'bg-amber-400'
                        }`}>
                          待機{waitingDays(e.waiting_since)}日
                        </span>
                      ) : cfg ? (
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full text-white ${cfg.badge}`}>
                          {cfg.label}
                        </span>
                      ) : e._days !== null ? (
                        <span className="text-xs text-slate-400">残{e._bizDays ?? e._days}営業日</span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                      {e._extStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white flex items-center gap-0.5 ${
                          e._extStatus.daysToCheck <= 0 ? 'bg-red-400' :
                          e._extStatus.daysToCheck <= 3 ? 'bg-orange-400' : 'bg-amber-400'
                        }`}>
                          <i className="fas fa-clipboard-check text-[8px]"></i>
                          延長確認{e._extStatus.daysToCheck <= 0 ? `（${Math.abs(e._extStatus.daysToCheckBiz)}日超過）` : `（${e._extStatus.daysToCheckBiz}日後）`}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// BP ステータス内訳
// ────────────────────────────────────────────────
function BPStatusBreakdown({ partners }) {
  const total = partners.length;
  const buckets = [
    { key: "active",   label: "稼働中",   color: "bg-violet-500", textColor: "text-violet-700" },
    { key: "upcoming", label: "稼働予定", color: "bg-blue-400",   textColor: "text-blue-700"   },
    { key: "inactive", label: "契約終了",   color: "bg-slate-400",  textColor: "text-slate-600"  },
  ];
  return (
    <div className="space-y-3">
      {buckets.map((b) => {
        const count = partners.filter(p => p.status === b.key).length;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={b.key}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={`font-bold ${b.textColor}`}>{b.label}</span>
              <span className="text-slate-500">{count}名 <span className="text-slate-400">({pct.toFixed(0)}%)</span></span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${b.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────
// BP テーブル
// ────────────────────────────────────────────────
function BPTable({ partners }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewMode, setViewMode] = useState("action"); // "action" | "all"

  const STATUS_LABELS = { active: "稼働中", upcoming: "稼働予定", inactive: "契約終了" };
  const statusColor = (s) =>
    s === "active" ? "bg-violet-100 text-violet-700" :
    s === "upcoming" ? "bg-blue-100 text-blue-700" :
    "bg-slate-100 text-slate-600";

  const rows = useMemo(() => {
    return partners
      .map((p) => ({ ...p, _days: daysUntil(p.contract_end), _bizDays: businessDaysUntil(p.contract_end), _extStatus: extensionAlertStatus(p.contract_end) }))
      .filter((p) => {
        // 要対応フィルター: 30日以内に終了 OR 非稼働 OR 延長確認対象
        if (viewMode === "action") {
          const needsAction =
            (p._days !== null && p._days <= 30) ||
            p.status === "inactive" ||
            p._extStatus !== null;
          if (!needsAction) return false;
        }
        if (filterStatus && p.status !== filterStatus) return false;
        if (search) {
          const q = search.toLowerCase();
          return p.name?.toLowerCase().includes(q) ||
                 p.partner_company?.toLowerCase().includes(q) ||
                 p.client_company?.toLowerCase().includes(q) ||
                 p.planner?.toLowerCase().includes(q);
        }
        return true;
      })
      .sort((a, b) => {
        if (a._days === null && b._days === null) return 0;
        if (a._days === null) return 1;
        if (b._days === null) return -1;
        return a._days - b._days;
      });
  }, [partners, search, filterStatus, viewMode]);

  // 要対応件数（バッジ用）
  const actionCount = useMemo(() => partners.filter(p => {
    const d = daysUntil(p.contract_end);
    return (d !== null && d <= 30) || p.status === "inactive" || extensionAlertStatus(p.contract_end) !== null;
  }).length, [partners]);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* トグル */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode("action")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              viewMode === "action" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className="fas fa-exclamation-circle text-[10px]" />
            要対応のみ
            {actionCount > 0 && (
              <span className={`text-[10px] font-bold px-1.5 rounded-full ${
                viewMode === "action" ? "bg-orange-100 text-orange-600" : "bg-slate-200 text-slate-500"
              }`}>{actionCount}</span>
            )}
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              viewMode === "all" ? "bg-white text-slate-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            全員
          </button>
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
          <input
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            placeholder="名前・BP会社・客先で検索..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">すべてのステータス</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-xs text-slate-400 ml-auto">{rows.length}件</span>
      </div>
      <div className="overflow-auto rounded-xl border border-slate-100 shadow-sm bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">氏名</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">ステータス</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">BP会社</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">客先</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">担当営業</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">甲単価</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">乙単価</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">粗利</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">契約終了</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">残日数</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-slate-400 text-sm">
                {viewMode === "action"
                  ? <span className="flex items-center justify-center gap-2 text-emerald-500"><i className="fas fa-check-circle text-base" />現在、対応が必要なBPエンジニアはいません</span>
                  : "該当なし"
                }
              </td></tr>
            ) : rows.map((p) => {
              const cfg = urgencyConfig(p._days, p._bizDays);
              const gross = (p.client_unit_price && p.partner_unit_price)
                ? Number(p.client_unit_price) - Number(p.partner_unit_price)
                : null;
              return (
                <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{p.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor(p.status)}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{p.partner_company || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{p.client_company || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{p.planner || <span className="text-slate-300">-</span>}</td>
                  <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">
                    {p.client_unit_price ? `¥${Number(p.client_unit_price).toLocaleString()}` : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">
                    {p.partner_unit_price ? `¥${Number(p.partner_unit_price).toLocaleString()}` : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {gross !== null ? (
                      <span className={`font-bold text-sm ${gross >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        ¥{gross.toLocaleString()}
                      </span>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    {p.contract_end
                      ? new Date(p.contract_end).toLocaleDateString("ja-JP", { year: "numeric", month: "short", day: "numeric" })
                      : <span className="text-slate-300">未設定</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1 items-start">
                      {cfg ? (
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full text-white ${cfg.badge}`}>{cfg.label}</span>
                      ) : p._days !== null ? (
                        <span className="text-xs text-slate-400">残{p._bizDays ?? p._days}営業日</span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                      {p._extStatus && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white flex items-center gap-0.5 ${
                          p._extStatus.daysToCheck <= 0 ? 'bg-red-400' :
                          p._extStatus.daysToCheck <= 3 ? 'bg-orange-400' : 'bg-amber-400'
                        }`}>
                          <i className="fas fa-clipboard-check text-[8px]"></i>
                          延長確認{p._extStatus.daysToCheck <= 0 ? `（${Math.abs(p._extStatus.daysToCheckBiz)}日超過）` : `（${p._extStatus.daysToCheckBiz}日後）`}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// 月次レポート
// ────────────────────────────────────────────────
function fmtYM(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${y}年${Number(m)}月`;
}

function MonthlyProjectReport({ idrEngineers, partnerEngineers }) {
  const today = new Date();
  const currentYM = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const autoIdr   = idrEngineers.filter(e => e.engineer_status === "アサイン済").length;
  const autoBp    = partnerEngineers.filter(e => ["active", "upcoming"].includes(e.status)).length;
  const autoTotal = autoIdr + autoBp;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingYM, setEditingYM] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ year_month: "", idr_count: "", bp_count: "", note: "" });

  // 一覧取得
  const fetchReports = async () => {
    try {
      const res = await fetch(`${API_BASE}/monthly-reports/`);
      const data = await res.json();
      setReports(Array.isArray(data) ? data : (data.results || []));
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  // 初期表示：今月レコードをDB上で確実に作成・同期
  useEffect(() => {
    const init = async () => {
      try {
        await fetch(`${API_BASE}/monthly-reports/ensure_current/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idr_count: autoIdr, bp_count: autoBp }),
        });
      } catch { /* ignore */ }
      fetchReports();
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // エンジニアデータ変更時に今月の自動算出を更新
  useEffect(() => {
    const syncCurrent = async () => {
      const cur = reports.find(r => r.year_month === currentYM);
      if (!cur || !cur.is_auto || cur.locked) return;
      try {
        await fetch(`${API_BASE}/monthly-reports/${cur.id}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idr_count: autoIdr, bp_count: autoBp, is_auto: true }),
        });
        setReports(prev => prev.map(r =>
          r.id === cur.id ? { ...r, idr_count: autoIdr, bp_count: autoBp, total_count: autoIdr + autoBp } : r
        ));
      } catch { /* ignore */ }
    };
    if (reports.length > 0) syncCurrent();
  }, [autoIdr, autoBp]); // eslint-disable-line react-hooks/exhaustive-deps

  // 降順ソート＋純増計算
  const sorted = [...reports].sort((a, b) => b.year_month.localeCompare(a.year_month));
  const withNet = sorted.map((r, i) => {
    const prev = sorted[i + 1];
    return {
      ...r,
      net_increase: prev != null ? r.total_count - prev.total_count : null,
      net_idr:      prev != null ? r.idr_count   - prev.idr_count   : null,
      net_bp:       prev != null ? r.bp_count    - prev.bp_count    : null,
    };
  });

  // チャート用直近12ヶ月（昇順）
  const chartData = [...withNet].reverse().slice(-12);
  const maxVal    = Math.max(...chartData.map(r => r.total_count), 1);

  const handleEdit = (r) => {
    setEditingYM(r.year_month);
    setEditForm({ idr_count: r.idr_count, bp_count: r.bp_count, note: r.note || "" });
  };

  const handleSaveEdit = async (ym) => {
    const r = reports.find(x => x.year_month === ym);
    if (!r) return;
    const idr = Math.max(Number(editForm.idr_count) || 0, 0);
    const bp  = Math.max(Number(editForm.bp_count)  || 0, 0);
    try {
      const res = await fetch(`${API_BASE}/monthly-reports/${r.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idr_count: idr, bp_count: bp, note: editForm.note, is_auto: false }),
      });
      if (res.ok) {
        const saved = await res.json();
        setReports(prev => prev.map(x => x.id === saved.id ? saved : x));
      }
    } catch { /* ignore */ }
    setEditingYM(null);
  };

  const handleLock = async (ym) => {
    const r = reports.find(x => x.year_month === ym);
    if (!r) return;
    try {
      const res = await fetch(`${API_BASE}/monthly-reports/${r.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: true, is_auto: false }),
      });
      if (res.ok) {
        const saved = await res.json();
        setReports(prev => prev.map(x => x.id === saved.id ? saved : x));
      }
    } catch { /* ignore */ }
  };

  const handleUnlock = async (ym) => {
    const r = reports.find(x => x.year_month === ym);
    if (!r) return;
    try {
      const res = await fetch(`${API_BASE}/monthly-reports/${r.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: false }),
      });
      if (res.ok) {
        const saved = await res.json();
        setReports(prev => prev.map(x => x.id === saved.id ? saved : x));
      }
    } catch { /* ignore */ }
  };

  const handleConfirmAdd = async () => {
    if (!addForm.year_month) return;
    const idr = Math.max(Number(addForm.idr_count) || 0, 0);
    const bp  = Math.max(Number(addForm.bp_count)  || 0, 0);
    try {
      const res = await fetch(`${API_BASE}/monthly-reports/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year_month: addForm.year_month, idr_count: idr, bp_count: bp, note: addForm.note, is_auto: false, locked: false }),
      });
      if (res.ok) {
        const saved = await res.json();
        setReports(prev => [saved, ...prev]);
      }
    } catch { /* ignore */ }
    setShowAddModal(false);
    setAddForm({ year_month: "", idr_count: "", bp_count: "", note: "" });
  };

  const currentNetIdr = withNet.find(r => r.year_month === currentYM)?.net_idr;
  const currentNetBp  = withNet.find(r => r.year_month === currentYM)?.net_bp;

  if (loading) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm animate-pulse">読み込み中...</div>;
  }

  return (
    <div className="space-y-5">
      {/* KPIカード */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <p className="text-xs text-slate-400 font-medium mb-1">今月プロジェクト総数</p>
          <p className="text-3xl font-bold text-slate-800">{autoTotal}<span className="text-sm font-normal text-slate-400 ml-1">件</span></p>
          <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>自動算出（IDR+BP）
          </p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100">
          <p className="text-xs text-slate-400 font-medium mb-1">IDR稼働中</p>
          <p className="text-3xl font-bold text-blue-600">{autoIdr}<span className="text-sm font-normal text-slate-400 ml-1">名</span></p>
          <p className="text-xs text-slate-400 mt-1">アサイン済エンジニア</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-indigo-100">
          <p className="text-xs text-slate-400 font-medium mb-1">BP稼働中/予定</p>
          <p className="text-3xl font-bold text-indigo-600">{autoBp}<span className="text-sm font-normal text-slate-400 ml-1">名</span></p>
          <p className="text-xs text-slate-400 mt-1">active / upcoming</p>
        </div>
        <div className={`bg-white rounded-2xl p-5 shadow-sm border ${currentNetIdr == null ? "border-slate-100" : currentNetIdr >= 0 ? "border-blue-100" : "border-red-100"}`}>
          <p className="text-xs text-slate-400 font-medium mb-1">IDR純増（前月比）</p>
          <p className={`text-3xl font-bold ${currentNetIdr == null ? "text-slate-300" : currentNetIdr >= 0 ? "text-blue-600" : "text-red-500"}`}>
            {currentNetIdr == null ? "--" : (currentNetIdr >= 0 ? "+" : "") + currentNetIdr}
          </p>
          <p className="text-xs text-slate-400 mt-1">{currentNetIdr == null ? "前月データなし" : `前月比 ${currentNetIdr >= 0 ? "+" : ""}${currentNetIdr}件`}</p>
        </div>
        <div className={`bg-white rounded-2xl p-5 shadow-sm border ${currentNetBp == null ? "border-slate-100" : currentNetBp >= 0 ? "border-indigo-100" : "border-red-100"}`}>
          <p className="text-xs text-slate-400 font-medium mb-1">BP純増（前月比）</p>
          <p className={`text-3xl font-bold ${currentNetBp == null ? "text-slate-300" : currentNetBp >= 0 ? "text-indigo-600" : "text-red-500"}`}>
            {currentNetBp == null ? "--" : (currentNetBp >= 0 ? "+" : "") + currentNetBp}
          </p>
          <p className="text-xs text-slate-400 mt-1">{currentNetBp == null ? "前月データなし" : `前月比 ${currentNetBp >= 0 ? "+" : ""}${currentNetBp}件`}</p>
        </div>
      </div>

      {/* 棒グラフ */}
      {chartData.length > 1 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <i className="fas fa-chart-bar text-amber-500 text-sm"></i>
              月次推移（直近12ヶ月）
            </h3>
            <div className="flex items-center gap-4 ml-auto text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-blue-400 inline-block"></span>IDR</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-indigo-400 inline-block"></span>BP</span>
            </div>
          </div>
          <div className="flex items-end gap-2 h-36">
            {chartData.map((r) => {
              const barH = Math.max(Math.round((r.total_count / maxVal) * 108), r.total_count > 0 ? 4 : 0);
              const idrFlex = r.idr_count || 0.001;
              const bpFlex  = r.bp_count  || 0.001;
              return (
                <div key={r.year_month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  {r.total_count > 0 && <span className="text-[10px] text-slate-500 font-medium">{r.total_count}</span>}
                  <div className="w-full flex flex-col rounded-t overflow-hidden" style={{ height: `${barH}px` }}>
                    <div style={{ flex: bpFlex,  backgroundColor: "#818cf8" }}></div>
                    <div style={{ flex: idrFlex, backgroundColor: "#60a5fa" }}></div>
                  </div>
                  <span className="text-[9px] text-slate-400 truncate">{Number(r.year_month.split("-")[1])}月</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 月次テーブル */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-700">月次プロジェクト記録</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors font-medium"
          >
            <i className="fas fa-plus text-[10px]"></i> 過去月を追加
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wide">年月</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-blue-400 uppercase tracking-wide">IDR</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-indigo-400 uppercase tracking-wide">BP</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-wide">総数</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-wide">純増</th>
              <th className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wide">メモ</th>
              <th className="px-5 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wide">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {withNet.map((r) => {
              const isCurrent = r.year_month === currentYM;
              const isEditing = editingYM === r.year_month;
              const ni = r.net_increase;
              return (
                <tr key={r.year_month} className={`${isCurrent ? "bg-amber-50/40" : ""} hover:bg-slate-50/60 transition-colors`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{fmtYM(r.year_month)}</span>
                      {isCurrent && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">今月</span>}
                      {r.is_auto && !r.locked && <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full">自動</span>}
                      {r.locked && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1"><i className="fas fa-lock text-[8px]"></i>確定</span>}
                    </div>
                  </td>
                  {isEditing ? (
                    <>
                      <td className="px-5 py-2">
                        <input type="number" min="0" className="w-16 text-right border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" value={editForm.idr_count} onChange={e => setEditForm(f => ({ ...f, idr_count: e.target.value }))} />
                      </td>
                      <td className="px-5 py-2">
                        <input type="number" min="0" className="w-16 text-right border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" value={editForm.bp_count} onChange={e => setEditForm(f => ({ ...f, bp_count: e.target.value }))} />
                      </td>
                      <td className="px-5 py-2 text-right font-bold text-slate-700">
                        {(Math.max(Number(editForm.idr_count) || 0, 0)) + (Math.max(Number(editForm.bp_count) || 0, 0))}
                      </td>
                      <td className="px-5 py-2"></td>
                      <td className="px-5 py-2">
                        <input className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" placeholder="メモ" value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
                      </td>
                      <td className="px-5 py-2">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => handleSaveEdit(r.year_month)} className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-lg transition-colors">保存</button>
                          <button onClick={() => setEditingYM(null)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1">取消</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 text-right font-semibold text-blue-600">{r.idr_count}</td>
                      <td className="px-5 py-3 text-right font-semibold text-indigo-600">{r.bp_count}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-800">{r.total_count}</td>
                      <td className="px-5 py-3 text-right">
                        {ni == null
                          ? <span className="text-slate-300 text-xs">--</span>
                          : <span className={`font-bold text-sm ${ni > 0 ? "text-emerald-600" : ni < 0 ? "text-red-500" : "text-slate-400"}`}>{ni > 0 ? "+" : ""}{ni}</span>
                        }
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs max-w-[200px] truncate">{r.note || <span className="text-slate-200">--</span>}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {!r.locked ? (
                            <>
                              <button onClick={() => handleEdit(r)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-amber-100 text-slate-400 hover:text-amber-600 flex items-center justify-center transition-colors" title="編集">
                                <i className="fas fa-pen text-[10px]"></i>
                              </button>
                              {!isCurrent && (
                                <button onClick={() => handleLock(r.year_month)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors" title="確定する">
                                  <i className="fas fa-lock text-[10px]"></i>
                                </button>
                              )}
                            </>
                          ) : (
                            <button onClick={() => handleUnlock(r.year_month)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-amber-100 text-slate-400 hover:text-amber-600 flex items-center justify-center transition-colors" title="確定解除">
                              <i className="fas fa-unlock text-[10px]"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 過去月追加モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[380px] p-6 space-y-4">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <i className="fas fa-calendar-plus text-amber-500"></i> 過去月を追加
            </h2>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">年月</label>
              <input type="month" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" value={addForm.year_month} max={currentYM} onChange={e => setAddForm(f => ({ ...f, year_month: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">IDR件数</label>
                <input type="number" min="0" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" value={addForm.idr_count} onChange={e => setAddForm(f => ({ ...f, idr_count: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">BP件数</label>
                <input type="number" min="0" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" value={addForm.bp_count} onChange={e => setAddForm(f => ({ ...f, bp_count: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">メモ（任意）</label>
              <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" placeholder="例: 新規参画2名" value={addForm.note} onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))} />
            </div>
            <p className="text-xs text-slate-400">※ 同じ年月のデータは上書きできません（既存を編集してください）</p>
            <div className="flex gap-3">
              <button onClick={handleConfirmAdd} disabled={!addForm.year_month} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 rounded-xl text-sm transition-colors disabled:opacity-40">追加する</button>
              <button onClick={() => setShowAddModal(false)} className="px-4 border border-slate-200 text-slate-500 rounded-xl text-sm hover:bg-slate-50">キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────
// メインコンポーネント
// ────────────────────────────────────────────────
export default function UtilizationDashboard() {
  const [engineers, setEngineers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [engineerTab, setEngineerTab] = useState("idr"); // "idr" | "bp"

  const fetchAll = async () => {
    try {
      const [engRes, bpRes] = await Promise.all([
        fetch(`${API_BASE}/engineers/`),
        fetch(`${API_BASE}/partner-engineers/`),
      ]);
      if (engRes.ok) setEngineers(await engRes.json());
      if (bpRes.ok) setPartners(await bpRes.json());
      setLastFetched(new Date());
    } catch (e) {
      console.error("データ取得エラー:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // ─── IDR 集計 ─────────────────────────────
  const idrStats = useMemo(() => {
    const total = engineers.length;
    const assigned = engineers.filter((e) => e.engineer_status === "アサイン済").length;
    const waiting = engineers.filter((e) => e.engineer_status === "未アサイン").length;
    const utilizationRate = total > 0 ? (assigned / total) * 100 : 0;
    const totalRevenue = engineers
      .filter(e => e.engineer_status === "アサイン済" && e.monthly_rate)
      .reduce((s, e) => s + Number(e.monthly_rate), 0);
    const idrAlerts = engineers
      .map((e) => ({ ...e, _days: daysUntil(e.project_end_date), _bizDays: businessDaysUntil(e.project_end_date), _type: 'idr' }))
      .filter((e) => e._days !== null && e._days <= 30)
      .sort((a, b) => a._days - b._days);
    const critical = idrAlerts.filter((e) => e._days <= 14).length;
    const warning  = idrAlerts.filter((e) => e._days > 14 && e._days <= 30).length;
    return { total, assigned, waiting, utilizationRate, totalRevenue, alerts: idrAlerts, critical, warning };
  }, [engineers]);

  // ─── BP 集計 ──────────────────────────────
  const bpStats = useMemo(() => {
    const total = partners.length;
    const active = partners.filter(p => p.status === 'active').length;
    const free = partners.filter(p => p.status === 'free').length;
    const utilizationRate = total > 0 ? (active / total) * 100 : 0;
    const totalGross = partners
      .filter(p => p.status === 'active' && p.client_unit_price && p.partner_unit_price)
      .reduce((s, p) => s + (Number(p.client_unit_price) - Number(p.partner_unit_price)), 0);
    const avgGrossRate = (() => {
      const valid = partners.filter(p => p.client_unit_price && p.partner_unit_price && Number(p.client_unit_price) > 0);
      if (!valid.length) return 0;
      const rates = valid.map(p => (Number(p.client_unit_price) - Number(p.partner_unit_price)) / Number(p.client_unit_price) * 100);
      return rates.reduce((s, r) => s + r, 0) / rates.length;
    })();
    const bpAlerts = partners
      .map((p) => ({ ...p, _days: daysUntil(p.contract_end), _bizDays: businessDaysUntil(p.contract_end), _type: 'bp' }))
      .filter((p) => p._days !== null && p._days <= 30)
      .sort((a, b) => a._days - b._days);
    const critical = bpAlerts.filter((p) => p._days <= 14).length;
    const warning  = bpAlerts.filter((p) => p._days > 14 && p._days <= 30).length;
    return { total, active, free, utilizationRate, totalGross, avgGrossRate, alerts: bpAlerts, critical, warning };
  }, [partners]);

  // ─── 統合アラート（IDR + BP 合算）─────────
  const combinedAlerts = useMemo(() => {
    return [...idrStats.alerts, ...bpStats.alerts].sort((a, b) => a._days - b._days);
  }, [idrStats.alerts, bpStats.alerts]);

  const combinedCritical = combinedAlerts.filter(e => e._days <= 14).length;

  // ─── 延長確認アラート（IDR + BP 合算）────
  const extensionAlerts = useMemo(() => {
    const idrItems = engineers
      .filter(e => e.project_end_date && extensionAlertStatus(e.project_end_date))
      .map(e => ({ ...e, _type: 'idr', _extStatus: extensionAlertStatus(e.project_end_date) }));
    const bpItems = partners
      .filter(p => p.contract_end && extensionAlertStatus(p.contract_end))
      .map(p => ({ ...p, _type: 'bp', _extStatus: extensionAlertStatus(p.contract_end) }));
    return [...idrItems, ...bpItems].sort((a, b) => a._extStatus.daysToCheck - b._extStatus.daysToCheck);
  }, [engineers, partners]);

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-prodia-background">
        <LoadingState message="データを取得中..." className="flex-1" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-prodia-background overflow-auto">
      <PageHeader
        title="稼働率ダッシュボード"
        description="契約終了アラート付き · リアルタイム管理"
        actions={
          <>
            {combinedCritical > 0 && (
              <Badge variant="danger" className="animate-pulse">
                <i className="fas fa-exclamation-triangle text-[10px]"></i>
                緊急アラート {combinedCritical}件
              </Badge>
            )}
            <Button variant="secondary" size="sm" onClick={fetchAll}>
              <i className="fas fa-sync-alt text-[10px]"></i>
              更新
            </Button>
            {lastFetched && (
              <span className="text-[11px] text-prodia-text-muted">
                最終更新: {lastFetched.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </>
        }
      />

      <div className="flex-1 px-4 sm:px-6 py-5 space-y-6">

        {/* ─────────── 統合 契約終了アラート（IDR + BP 常時表示）─────────── */}
        <div className="bg-prodia-surface rounded-xl border border-prodia-border shadow-prodia p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <i className="fas fa-bell text-orange-400"></i>
              契約終了アラート
              <span className="ml-1 text-[11px] font-normal text-slate-400">（IDR + BP · 30日以内）</span>
            </h2>
            <div className="flex items-center gap-2">
              {combinedAlerts.filter(e => e._days <= 14 && e._days >= 0).length > 0 && (
                <span className="text-xs font-bold bg-red-50 text-red-600 px-2.5 py-0.5 rounded-full">
                  緊急 {combinedAlerts.filter(e => e._days <= 14 && e._days >= 0).length}件
                </span>
              )}
              {combinedAlerts.length > 0 && (
                <span className="text-xs font-bold bg-orange-50 text-orange-600 px-2.5 py-0.5 rounded-full">
                  計{combinedAlerts.length}件
                </span>
              )}
            </div>
          </div>
          {combinedAlerts.length === 0 ? (
            <div className="text-center py-6 text-slate-300">
              <i className="fas fa-check-circle text-2xl mb-2 block text-emerald-300"></i>
              <p className="text-sm">30日以内に終了する契約はありません</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {combinedAlerts.map((e) => {
                const cfg = urgencyConfig(e._days, e._bizDays);
                return cfg ? <AlertRow key={`${e._type}-${e.id}`} engineer={e} days={e._days} cfg={cfg} /> : null;
              })}
            </div>
          )}
        </div>

        {/* ─────────── 延長確認アラート ─────────── */}
        <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <i className="fas fa-clipboard-check text-amber-500"></i>
              延長確認アラート
              <span className="ml-1 text-[11px] font-normal text-slate-400">（終了1ヶ月前の週の月曜日に延長有無を確認）</span>
            </h2>
            <div className="flex items-center gap-2">
              {extensionAlerts.filter(e => e._extStatus.daysToCheck <= 0).length > 0 && (
                <span className="text-xs font-bold bg-red-50 text-red-600 px-2.5 py-0.5 rounded-full animate-pulse">
                  要確認 {extensionAlerts.filter(e => e._extStatus.daysToCheck <= 0).length}件
                </span>
              )}
              {extensionAlerts.length > 0 && (
                <span className="text-xs font-bold bg-amber-50 text-amber-600 px-2.5 py-0.5 rounded-full">
                  計{extensionAlerts.length}件
                </span>
              )}
            </div>
          </div>
          {extensionAlerts.length === 0 ? (
            <div className="text-center py-6 text-slate-300">
              <i className="fas fa-check-circle text-2xl mb-2 block text-emerald-300"></i>
              <p className="text-sm">現在、延長確認が必要なエンジニアはいません</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {extensionAlerts.map(e => (
                <ExtensionCheckRow key={`ext-${e._type}-${e.id}`} engineer={e} />
              ))}
            </div>
          )}
        </div>

        {/* ─────────── IDR / BP タブ ─────────── */}
        <div className="flex items-center gap-2 bg-slate-100 rounded-2xl p-1 w-fit">
          <button
            onClick={() => setEngineerTab("idr")}
            className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
              engineerTab === "idr" ? "bg-white shadow-sm text-emerald-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className="fas fa-user-tie" />
            IDR
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              engineerTab === "idr" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
            }`}>{idrStats.total}名</span>
          </button>
          <button
            onClick={() => setEngineerTab("bp")}
            className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
              engineerTab === "bp" ? "bg-white shadow-sm text-violet-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className="fas fa-handshake" />
            BP
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              engineerTab === "bp" ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-500"
            }`}>{bpStats.total}名</span>
          </button>
          <button
            onClick={() => setEngineerTab("monthly")}
            className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
              engineerTab === "monthly" ? "bg-white shadow-sm text-amber-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className="fas fa-chart-bar" />
            月次レポート
          </button>
        </div>

        {/* ─────────── IDRタブ ─────────── */}
        {engineerTab === "idr" && <>
          {/* KPI カード */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard label="総IDR数" value={idrStats.total} suffix="名" icon="fas fa-users" accent="primary" />
            <KpiCard label="稼働中" value={idrStats.assigned} suffix="名" sub="アサイン済" icon="fas fa-user-check" accent="success" />
            <KpiCard label="待機中" value={idrStats.waiting} suffix="名" sub="未アサイン" icon="fas fa-user-clock" accent={idrStats.waiting > 0 ? "warning" : "primary"} />
            <StatCard
              label="月売上合計"
              value={idrStats.totalRevenue >= 1000000
                ? `¥${(idrStats.totalRevenue / 1000000).toFixed(1)}M`
                : `¥${idrStats.totalRevenue.toLocaleString()}`}
              sub="稼働中エンジニア"
              icon="fas fa-yen-sign"
              accent="info"
            />
            <StatCard
              label="稼働率"
              value={`${idrStats.utilizationRate.toFixed(0)}%`}
              sub={idrStats.utilizationRate >= 80 ? "良好" : idrStats.utilizationRate >= 60 ? "要注意" : "危険水域"}
              icon="fas fa-chart-pie"
              accent={idrStats.utilizationRate >= 80 ? "success" : idrStats.utilizationRate >= 60 ? "warning" : "danger"}
            />
          </div>

          {/* ゲージ + テーブル */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h2 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
                <i className="fas fa-chart-pie text-emerald-500"></i>稼働率
              </h2>
              <UtilizationGauge rate={idrStats.utilizationRate} />
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">ステータス内訳</p>
                <StatusBreakdown engineers={engineers} />
              </div>
            </div>
            <div className="lg:col-span-3">
              <EngineerTable engineers={engineers} />
            </div>
          </div>
        </>}

        {/* ─────────── BPタブ ─────────── */}
        {engineerTab === "bp" && <>
          {/* KPI カード */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="総BP数" value={bpStats.total} suffix="名" icon="fas fa-handshake" accent="primary" />
            <KpiCard label="稼働中" value={bpStats.active} suffix="名" sub="稼働中" icon="fas fa-user-check" accent="info" />
            <StatCard
              label="稼働中粗利合計"
              value={bpStats.totalGross >= 1000000
                ? `¥${(bpStats.totalGross / 1000000).toFixed(1)}M`
                : `¥${bpStats.totalGross.toLocaleString()}`}
              sub="甲-乙単価合算"
              icon="fas fa-coins"
              accent="info"
            />
            <StatCard
              label="平均粗利率"
              value={`${bpStats.avgGrossRate.toFixed(1)}%`}
              sub={bpStats.avgGrossRate >= 20 ? "良好" : bpStats.avgGrossRate >= 10 ? "要確認" : "要改善"}
              icon="fas fa-percentage"
              accent={bpStats.avgGrossRate >= 20 ? "success" : bpStats.avgGrossRate >= 10 ? "warning" : "danger"}
            />
          </div>

          {/* ゲージ + テーブル */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h2 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
                <i className="fas fa-chart-pie text-violet-500"></i>稼働率
              </h2>
              <UtilizationGauge rate={bpStats.utilizationRate} />
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">ステータス内訳</p>
                <BPStatusBreakdown partners={partners} />
              </div>
            </div>
            <div className="lg:col-span-3">
              <BPTable partners={partners} />
            </div>
          </div>
        </>}

        {/* ─────────── 月次レポートタブ ─────────── */}
        {engineerTab === "monthly" && (
          <MonthlyProjectReport idrEngineers={engineers} partnerEngineers={partners} />
        )}

      </div>
    </div>
  );
}
